import { BackgammonTableType, MoveType } from "../../../../graphql/generated";
import { VirtualItemPurchaseFragmentFragment } from "../../common/game/hooks/graphql/VirtualItemPurchaseFragment.generated";
import { ChatMessage } from "../../../Chat/InlineChat/types";

export const MAX_LOSER_MULTIPLIER = 2;

export type ChatState = Array<ChatMessage>;

export type VirtualItemPurchasesState = {
  [key: string]: VirtualItemPurchaseFragmentFragment;
};

export type GameStatus =
  | "WAITING_FOR_PLAYERS"
  | "WAITING_CONFIRMATION_TO_START_GAME"
  | "ROUND_STARTED"
  | "ROUND_COMPLETED"
  | "WAITING_FOR_NEXT_ROUND"
  | "WAITING_FOR_TO_ASK_IF_NEW_TABLE"
  | "GAME_COMPLETED";

export type GameResult = "WIN" | "LOSE" | "DRAW";

export type RankingType = "GLOBAL" | "LEADERBOARD" | "TOURNAMENT";

export type DoubleProposalState =
  | "NOT_ALLOWED"
  | "NONE"
  | "DOUBLING"
  | "DOUBLE_CONFIRMED"
  | "REDOUBLING"
  | "REDOUBLE_CONFIRMED";

export type Ranking = {
  type: RankingType;
  name: string;
  ranking: number;
};

export type Result = {
  quit: boolean;
  debit: number;
  rankings: Array<Ranking>;
  result: GameResult;
};

export type Profile = {
  id: string;
  name: string;
  ready: boolean;
  online: boolean;
  autoPlay: number;
};

export type Score = [number, number];

export type ServerState = {
  meta: {
    name: string;
    tournament: boolean;
    bet: number;
    points: number;
    creator: string;
    type: BackgammonTableType;
    profiles: {
      [key: string]: Profile;
    };
    visitors: Array<string>;
    invitations: Array<string>;
    newTableId: string | null;
  };
  game: {
    status: GameStatus;
    timer: [number, number] | null;
    players: Array<string | null>;
    playerIndex: number | null;
    currentPlayer: number | null;
    results: Array<Result> | null;
    doWaitOnCubeAnswer: boolean;
    doubleBetProposalState: DoubleProposalState;
    doublingBetReceivedAnswers: Array<boolean>;
    doublePropositionsNumber: Array<number>;
    maxDoublePropositionsNumber: number;
    maxCubeValue: CubeValue;
    doubleBetProposalInitiator: string | null;
    usingDoubleCubeAllowed?: boolean;
    roundStartRolledDices?: Dice[];
    currentDices?: {
      first: Dice;
      second: Dice;
    };
    gameBetResult: "STANDARD" | "GAMMON" | "BACKGAMMON" | null;
    board?: Board;
    scores: Array<Array<number>>;
    movementType?: MoveType; // Add other movement types as needed
    allowedCheckerMovements?: AllowedCheckerMovements;
    allowedEnterPoints?: number[] | null;
    allowedBearingOffPoints?: AllowedCheckerMovements;
    pipsCounting?: number[]; // Tuple for pip counts for both players
    cube?: Cube;
    askCreateNewTable: Array<boolean | null>;
    isTurnHistoryEmpty: boolean;
    isResign: boolean;
    notEnoughChipsForCubeDoubling: boolean;
    isBotGame: boolean;
    matchTournamentType: string | null;
    matchTournamentId: string | null;
  };
};

export enum CubeValue {
  ONE = "ONE",
  TWO = "TWO",
  FOUR = "FOUR",
  EIGHT = "EIGHT",
  SIXTEEN = "SIXTEEN",
  THIRTY_TWO = "THIRTY_TWO",
  SIXTY_FOUR = "SIXTY_FOUR",
}
export type Dice = {
  value: number;
  isUsed: boolean;
};

type CheckerColour = "RED" | "WHITE" | null;

export type BoardPoint = {
  index: number;
  numberOfCheckers: number;
  checkerColour: CheckerColour;
};

type Board = {
  points: BoardPoint[];
  barWhite: number;
  barRed: number;
  bearOffWhite: number;
  bearOffRed: number;
};

type Cube = {
  value: CubeValue; // Assuming possible values
  ownerPlayerIndex: number | null;
  doublingProposerIndex: number | null;
  isCubeAccepted: boolean;
};

export type AllowedCheckerMovements = {
  [key: string]: number[]; // Dynamic keys like "p_12" with array of indices
};

export type GameState = ServerState["game"];
export type MetaState = ServerState["meta"];
export type InternalState = {
  tableId: string;
  userId: string;
  isPlayer: boolean;
  isMyTurn: boolean;
  error: string | null;
  completed: boolean;
  initialized: boolean;
  hasProgress: boolean;
};

export type RootState = {
  game: GameState;
  meta: MetaState;
  chat: ChatState;
  virtualItems: VirtualItemPurchasesState;
  internal: InternalState;
};
